import { useMemo } from 'react'
import type { JSX } from 'react'

import { pageHeaderStyles, useIsNarrowPageHeader } from '@buffer-mono/popcorn'

import type { MaskedFilterByChannelFragment } from '~publish/components/FilterByChannel'
import {
  FilterByChannel,
  useFilterByChannelFragment,
} from '~publish/components/FilterByChannel'
import type { Service } from '~publish/pages/Community/graphqlEnums'
import { VIEW_ONLY_COMMUNITY_CHANNEL_TOOLTIP } from '~publish/pages/Community/shared/viewOnlyScenario'
import { selectEnabledChannelIds } from '~publish/utils/permissions/effectiveChannelSelection'

import { BulkActionsMenu } from './BulkActionsMenu/index'
import { BulkActionsCoachmark } from './BulkActionsCoachmark'
import type { CoachmarkType } from './BulkActionsCoachmark'
import { FilterAndSortMenu } from './FilterAndSortMenu'
import { FilterStatusMenu } from './FilterStatusMenu'
import {
  DEFAULT_DATE_RANGE,
  DEFAULT_SORT_OPTION,
  DEFAULT_STATUS,
} from './types'
import type {
  CommunityEntity,
  DateRange,
  FilterStatus,
  SortOption,
} from './types'
import { useFilters } from './useFilters'
import { useDismissalTracker } from '../hooks/tracking/useDismissalTracker'

export type CommunityFiltersProps = {
  // The setters arrive as props (rather than being read from useFilters like
  // the values) because the views wrap them with per-view tracking — see
  // useFilterHandlers.
  setStatus: (status: FilterStatus) => void
  setSortOption: (option: SortOption) => void
  setPublishedAt: (publishedAt: DateRange) => void
  // Channels the bulk-resolve action may target. The views pre-filter this to
  // channels the actor can manage (manageComments); view-only channels are
  // excluded. An empty array therefore means "nothing the actor can resolve",
  // which hides the bulk-resolve entry point entirely. See CORE-5996.
  channelIds: string[]
  postId?: string
  onResolvingChange?: (isResolving: boolean) => void
  showBannerDismissedCoachMark?: boolean
  onBannerDismissedCoachMarkDismiss?: () => void
  channelService?: Service
  // Optional: when provided, the channel filter is rendered (AllChannelsView only)
  channels?: MaskedFilterByChannelFragment
  disabledChannelIds?: Set<string>
  getDisabledChannelReason?: (channelId: string) => React.ReactNode
  viewOnlyChannelIds?: Set<string>
  // Switches the bulk-resolve menu item and the entity named in the filter
  // and resolve tooltips. Defaults to 'comments'.
  entity?: CommunityEntity
}

export const CommunityFilters = ({
  setStatus,
  setSortOption,
  setPublishedAt,
  channelIds,
  postId,
  onResolvingChange,
  showBannerDismissedCoachMark,
  onBannerDismissedCoachMarkDismiss,
  channelService,
  channels,
  disabledChannelIds,
  getDisabledChannelReason,
  viewOnlyChannelIds,
  entity = 'comments',
}: CommunityFiltersProps): JSX.Element => {
  const {
    filters: {
      status = DEFAULT_STATUS,
      sortOption,
      publishedAt = DEFAULT_DATE_RANGE,
      selectedChannelIds = [],
    },
    actions: { setSelectedChannelIds },
    utils: { clearFilters },
  } = useFilters()
  // Use header row width instead of viewport width to match CSS container query.
  // The collapsed filters live inside the filter/sort dropdown (portal-escaped),
  // so they read this hook rather than the `hideOnNarrow` CSS helper. Default to
  // true (show mobile filters) until width is measured, so there's never a
  // moment where neither desktop nor mobile filters are accessible.
  const isNarrowHeader = useIsNarrowPageHeader() ?? true
  const unmaskedChannels = useFilterByChannelFragment(channels ?? [])
  const { shouldShowCoachmark, onDismissCoachmark } =
    useDismissalTracker(channelIds)

  // The channels[] URL param is user-editable (bookmarks, hand-typed URLs), so
  // the selection the filters display is its intersection with the channels
  // the actor can use here — the views encode the Community rule (comments
  // capability + `viewComments`) in `disabledChannelIds`. A forced id for a
  // disabled channel must not render as selected. The data queries are already
  // scoped upstream (@see useAllChannelsData's `filteredChannels`).
  const effectiveSelectedChannelIds = useMemo(
    () =>
      selectEnabledChannelIds(
        selectedChannelIds,
        unmaskedChannels,
        disabledChannelIds,
      ),
    [selectedChannelIds, unmaskedChannels, disabledChannelIds],
  )

  // The channels the bulk-resolve action actually targets. When the channel
  // filter is shown and the actor has picked channels, scope to that selection;
  // otherwise fall back to every manageable channel (today's "act on all"
  // default).
  const targetChannelIds = useMemo(() => {
    if (!channels || selectedChannelIds.length === 0) return channelIds
    const selected = new Set(selectedChannelIds)
    return channelIds.filter((id) => selected.has(id))
  }, [channels, selectedChannelIds, channelIds])

  // Whether the actor can bulk-resolve at all: an empty target set means there's
  // nothing to resolve — either no manageable channels, or a selection that
  // contains none. Hide the bulk-resolve item (and the resolve coachmark that
  // points at it).
  const canBulkResolve = targetChannelIds.length > 0

  // The resolve menu is resolve-only: show it exactly when there's something to
  // resolve. Collapsed channel/status filters live under the filter/sort menu,
  // so the resolve trigger never doubles as a filter entry point.
  const showBulkActionsMenu = canBulkResolve

  // Determine active coachmark with priority: bannerDismissed > resolveComments
  const getActiveCoachmark = (): CoachmarkType | null => {
    if (showBannerDismissedCoachMark) return 'bannerDismissed'
    if (canBulkResolve && shouldShowCoachmark) return 'resolveComments'
    return null
  }

  const handleCoachmarkDismiss = (): void => {
    const activeCoachmark = getActiveCoachmark()
    if (activeCoachmark === 'bannerDismissed') {
      onBannerDismissedCoachMarkDismiss?.()
    } else if (activeCoachmark === 'resolveComments') {
      onDismissCoachmark()
    }
  }

  return (
    <>
      {channels && (
        <FilterByChannel
          channels={channels}
          value={effectiveSelectedChannelIds}
          onSelect={setSelectedChannelIds}
          className={pageHeaderStyles.hideOnNarrow}
          disabledChannelIds={disabledChannelIds}
          getDisabledChannelReason={getDisabledChannelReason}
          viewOnlyChannelIds={viewOnlyChannelIds}
          viewOnlyChannelReason={VIEW_ONLY_COMMUNITY_CHANNEL_TOOLTIP}
        />
      )}
      <FilterStatusMenu
        value={status}
        onChange={setStatus}
        className={pageHeaderStyles.hideOnNarrow}
        entity={entity}
      />
      <FilterAndSortMenu
        sortOption={sortOption ?? DEFAULT_SORT_OPTION}
        status={status}
        // The mentions BE sorts by published date only (see MentionsSortField
        // in @buffer-mono/entities), so "Unanswered first" is comments-only.
        supportsUnansweredFirst={entity !== 'mentions'}
        onSortOptionChange={setSortOption}
        publishedAt={publishedAt}
        onPublishedAtChange={setPublishedAt}
        onResetFilters={(): void => clearFilters()}
        // On narrow headers the inline channel/status controls collapse into
        // this menu, so all filtering stays under one funnel.
        showCollapsedFilters={isNarrowHeader}
        onStatusChange={setStatus}
        channels={channels ? unmaskedChannels : undefined}
        selectedChannelIds={effectiveSelectedChannelIds}
        onChannelSelect={setSelectedChannelIds}
        disabledChannelIds={disabledChannelIds}
        getDisabledChannelReason={getDisabledChannelReason}
        entity={entity}
      />
      {showBulkActionsMenu && (
        <BulkActionsMenu
          channelIds={targetChannelIds}
          postId={postId}
          channelService={channelService}
          onResolvingChange={onResolvingChange}
          entity={entity}
        >
          <BulkActionsCoachmark
            type={getActiveCoachmark()}
            onDismiss={handleCoachmarkDismiss}
          >
            <BulkActionsMenu.Trigger />
          </BulkActionsCoachmark>
          <BulkActionsMenu.Content>
            {/* Resolve-only menu. Bulk resolve is hidden when the actor manages
                no in-scope channel (view-only). See CORE-5996. */}
            {entity === 'mentions' ? (
              <BulkActionsMenu.ResolveMentionsItem />
            ) : (
              <BulkActionsMenu.ResolveCommentsItem />
            )}
          </BulkActionsMenu.Content>
        </BulkActionsMenu>
      )}
    </>
  )
}
